import { Injectable, isDevMode, signal } from '@angular/core';
import {
  applyTheme,
  argbFromHex,
  themeFromSourceColor,
} from '@material/material-color-utilities';
import { getSubdomain } from '../../core/utils/domain.utils';

export type TenantThemeMode = 'light' | 'dark' | 'system';

/**
 * Shape of the tenant brand configuration as persisted on the device.
 * Mirrors the backend `tenant.config` payload but typed for the fields we read.
 */
export interface TenantBrandConfig {
  primaryColor?: string;
  /** Full logo, light mode. Backend field: `longLogo`. */
  logoLight?: string;
  /** Full logo, dark mode. Backend field: `logoDark` (future). */
  logoDark?: string;
  /** Icon-only logo for the collapsed sidebar. Backend field: `squareLogo`. */
  logoCollapsed?: string;
  /** Catch-all for any additional fields the backend may add later. */
  [key: string]: unknown;
}

const TENANT_THEME_KEY = 'tenant_theme_mode';
const TENANT_CONFIG_KEY = 'tenant_brand_config';
const KIMBOCARE_DEFAULT_PRIMARY = '#005CBB';

@Injectable({
  providedIn: 'root',
})
export class TenantThemeService {
  private _theme = signal<TenantThemeMode>(this._loadSavedTheme());
  readonly theme = this._theme.asReadonly();

  /**
   * Reactive holder for the current tenant brand config (color + logo URLs).
   * Components binding logo `src` should read this signal so they pick up
   * changes when the tenant config is refreshed after login.
   */
  private _tenantConfig = signal<TenantBrandConfig | null>(null);
  readonly tenantConfig = this._tenantConfig.asReadonly();

  private _mediaQuery = window.matchMedia('(prefers-color-scheme: light)');

  /** Cached M3 theme object from material-color-utilities so dark/light toggle can re-apply */
  private _m3Theme: ReturnType<typeof themeFromSourceColor> | null = null;
  /** Cached primary hex for Tailwind CSS variables */
  private _currentPrimaryHex: string = KIMBOCARE_DEFAULT_PRIMARY;

  constructor() {
    this._mediaQuery.addEventListener('change', () => {
      if (this._theme() === 'system') {
        this._applyThemeToDom('system');
      }
    });
    this._applyThemeToDom(this._theme());

    // Restore cached tenant brand on tenant subdomains so the brand survives
    // logout and is visible on the login screen for returning users. Root
    // domain intentionally stays KimboCare-branded (no restore).
    if (getSubdomain() !== '') {
      const cached = this._loadCachedTenantConfig();
      if (cached) {
        this._applyTenantConfig(cached);
      }
    }

    // Dev-only: ?primaryColor=#hex in the URL overrides whatever was just
    // applied, so you can preview any color without backend round-trips.
    // Wins over the cached tenant config; not persisted (reload to revert).
    if (isDevMode()) {
      const devOverride = this._readDevPrimaryColorOverride();
      if (devOverride) {
        // eslint-disable-next-line no-console
        console.info(`[TenantTheme] dev primary color override: ${devOverride}`);
        this.applyTenantPrimaryColor(devOverride);
      }
    }
  }

  setTheme(mode: TenantThemeMode): void {
    this._theme.set(mode);
    localStorage.setItem(TENANT_THEME_KEY, mode);
    this._applyThemeToDom(mode);
  }

  /**
   * Persists the tenant brand config to localStorage and applies it.
   * Accepts the raw backend payload (which uses `longLogo` / `squareLogo`
   * naming); normalizes it into our `TenantBrandConfig` shape.
   *
   * After this is called once, subsequent app boots on the same device
   * re-apply the brand automatically via the constructor — even before login.
   */
  saveTenantConfig(rawConfig: Record<string, unknown> | null | undefined): void {
    if (!rawConfig) return;

    const normalized: TenantBrandConfig = {
      primaryColor: this._asString(rawConfig['primaryColor']),
      logoLight:
        this._asString(rawConfig['logoLight']) ??
        this._asString(rawConfig['longLogo']),
      logoDark: this._asString(rawConfig['logoDark']),
      logoCollapsed:
        this._asString(rawConfig['logoCollapsed']) ??
        this._asString(rawConfig['squareLogo']),
    };

    localStorage.setItem(TENANT_CONFIG_KEY, JSON.stringify(normalized));
    this._applyTenantConfig(normalized);
  }

  /**
   * Clears the cached tenant brand and reverts to the KimboCare default.
   * Call this when a user explicitly switches tenants or leaves the org.
   */
  clearTenantConfig(): void {
    localStorage.removeItem(TENANT_CONFIG_KEY);
    this._tenantConfig.set(null);
    this.applyTenantPrimaryColor(KIMBOCARE_DEFAULT_PRIMARY);
  }

  /**
   * Generates a full M3 theme from a hex color using Google's material-color-utilities
   * and applies it as CSS custom properties on <html>.
   *
   * This sets all --mat-sys-* tokens that Angular Material 19 components read,
   * plus --mat-tw-primary (RGB) and --tenant-primary-* for Tailwind consumption.
   */
  applyTenantPrimaryColor(primaryColor: string | undefined): void {
    const sourceColor = primaryColor || KIMBOCARE_DEFAULT_PRIMARY;
    this._currentPrimaryHex = sourceColor;

    let argb: number;
    let rgbColor: string | undefined;

    try {
      argb = argbFromHex(sourceColor);
      const r = (argb >> 16) & 0xff;
      const g = (argb >> 8) & 0xff;
      const b = argb & 0xff;
      rgbColor = `${r} ${g} ${b}`;
    } catch {
      argb = argbFromHex(KIMBOCARE_DEFAULT_PRIMARY);
    }

    // Generate the full M3 theme (light + dark palettes)
    this._m3Theme = themeFromSourceColor(argb);

    // Determine current dark/light mode
    const isDark = this._isDark();

    // Apply theme to <html> — this sets --md-sys-color-* variables
    const target = document.documentElement;
    applyTheme(this._m3Theme, {
      target,
      dark: isDark,
      brightnessSuffix: true,
    });

    // Map --md-sys-color-* → --mat-sys-* (Angular Material 19 reads --mat-sys-*)
    this._mapMdToMatSys(target);

    // Set Tailwind CSS variable (RGB space-separated for opacity support)
    if (rgbColor) {
      target.style.setProperty('--mat-tw-primary', rgbColor);
    }
    // Override --mat-sys-primary with the exact hex (applyTheme may adjust it slightly)
    target.style.setProperty('--mat-sys-primary', sourceColor);

    // Also set --tenant-primary-* for Tailwind palette classes
    this._setTailwindPalette(sourceColor);
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /** Applies a full tenant brand (color + logos) and updates the signal. */
  private _applyTenantConfig(config: TenantBrandConfig): void {
    this._tenantConfig.set(config);
    this.applyTenantPrimaryColor(config.primaryColor || KIMBOCARE_DEFAULT_PRIMARY);
  }

  private _loadCachedTenantConfig(): TenantBrandConfig | null {
    try {
      const raw = localStorage.getItem(TENANT_CONFIG_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as TenantBrandConfig) : null;
    } catch {
      // Corrupted JSON — drop it so we don't keep failing every boot.
      localStorage.removeItem(TENANT_CONFIG_KEY);
      return null;
    }
  }

  private _asString(value: unknown): string | undefined {
    return typeof value === 'string' && value.length > 0 ? value : undefined;
  }

  /**
   * Reads `?primaryColor=` from the URL. The app uses hash-based routing
   * (#/path?...) so the query string can live inside the hash too. Returns a
   * normalized `#RRGGBB`/`#RGB` string or null. Dev-only; not for production.
   */
  private _readDevPrimaryColorOverride(): string | null {
    const hash = window.location.hash;
    const queryFromHash = hash.includes('?')
      ? hash.substring(hash.indexOf('?') + 1)
      : '';
    const search = window.location.search.replace(/^\?/, '');
    const params = new URLSearchParams(queryFromHash || search);
    const raw = params.get('primaryColor');
    if (!raw) return null;

    const normalized = raw.startsWith('#') ? raw : `#${raw}`;
    return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(normalized)
      ? normalized
      : null;
  }

  private _isDark(): boolean {
    const mode = this._theme();
    return mode === 'dark' || (mode === 'system' && this._mediaQuery.matches);
  }

  /**
   * Iterates over the inline styles written by `applyTheme()` and copies
   * every `--md-sys-color-*` token to `--mat-sys-*` so Angular Material 19
   * components pick them up.
   */
  private _mapMdToMatSys(target: HTMLElement): void {
    const styles = target.style;
    for (const propName of Array.from(styles)) {
      if (propName.startsWith('--md-sys')) {
        const matPropName = '--mat-sys' + propName.replace('--md-sys-color', '');
        target.style.setProperty(
          matPropName,
          target.style.getPropertyValue(propName),
        );
      }
    }
  }

  /**
   * Sets Tailwind-compatible CSS variables (RGB space-separated) for the primary palette.
   */
  private _setTailwindPalette(hex: string): void {
    const base = this._hexToRgb(hex);
    if (!base) return;

    const mix = (c1: number[], c2: number[], w: number) =>
      c1.map((c, i) => Math.round(c * (w / 100) + c2[i] * (1 - w / 100))).join(' ');

    const white = [255, 255, 255];
    const black = [0, 0, 0];

    const palette: Record<string, string> = {
      '--tenant-primary-50': mix(base, white, 10),
      '--tenant-primary-100': mix(base, white, 20),
      '--tenant-primary-200': mix(base, white, 40),
      '--tenant-primary-300': mix(base, white, 60),
      '--tenant-primary-400': mix(base, white, 80),
      '--tenant-primary-500': mix(base, white, 100),
      '--tenant-primary-600': mix(base, black, 80),
      '--tenant-primary-700': mix(base, black, 60),
      '--tenant-primary-800': mix(base, black, 40),
      '--tenant-primary-900': mix(base, black, 20),
      '--tenant-primary': hex,
    };

    const root = document.documentElement;
    Object.entries(palette).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  }

  private _hexToRgb(hex: string): number[] | null {
    const clean = hex.replace('#', '');
    const full = clean.length === 3
      ? clean[0] + clean[0] + clean[1] + clean[1] + clean[2] + clean[2]
      : clean;
    const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(full);
    return result
      ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
      : null;
  }

  private _loadSavedTheme(): TenantThemeMode {
    const saved = localStorage.getItem(TENANT_THEME_KEY) as TenantThemeMode | null;
    return saved || 'system';
  }

  private _applyThemeToDom(mode: TenantThemeMode): void {
    const html = document.documentElement;
    const isDark = mode === 'dark' || (mode === 'system' && this._mediaQuery.matches);

    if (isDark) {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }

    // Re-apply the cached M3 theme with the new brightness if we have one
    if (this._m3Theme) {
      applyTheme(this._m3Theme, {
        target: html,
        dark: isDark,
        brightnessSuffix: true,
      });
      this._mapMdToMatSys(html);
      // Re-force exact primary hex
      html.style.setProperty('--mat-sys-primary', this._currentPrimaryHex);
    }
  }
}
